import { useState, useRef } from 'react'
import styled from 'styled-components'
import { usePathname, useSearchParams } from 'next/navigation'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { NextLink } from '@/features/shared/elements/Link'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { Row } from '@shipt/design-system-layouts'
import { type StandardNavData } from '@/features/cms/components/CMS/types'
import dynamic from 'next/dynamic'
import { ActivityPunchCardNavigationItem } from '@/features/shop/components/CategorySubNav/ActivityPunchCardNavigationItem'
import { HeaderItem } from '@/features/shop/components/CategorySubNav/HeaderItem'
import { isNavLinkActive } from '@/features/shop/components/CategorySubNav/utils/isNavLinkActive'
import { useNavOverflow } from '@/features/shop/components/CategorySubNav/utils/useNavOverflow'
import { useQueryCMSActivityPunchCard } from '@/features/cms/services/CMS/queries'

const DynamicHeaderRefreshMoreNavigationItem = dynamic(
  () =>
    import('@/features/shop/components/CategorySubNav/MoreNavigationItem').then(
      (mod) => mod.MoreNavigationItem,
    ),
  { ssr: false },
)

const itemPadding = 12

type Props = {
  globalHeaderCategories: StandardNavData['data']
  holidayGHP?: boolean
}

const handleHeaderItemClick = (type: string, index: number, url: string) => {
  trackElementClicked({
    type: 'link',
    display_format: DISPLAY_FORMAT.HORIZONTAL,
    display_sublocation: 'header navigation',
    list_index: 0,
    column_index: index,
    link_name: type,
    content: type,
    next_location: url,
  })
}

export const GlobalPriorityNavigation = ({ globalHeaderCategories, holidayGHP }: Props) => {
  const [itemsToDisplay, setItemsToDisplay] = useState(0)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { data: punchCardData } = useQueryCMSActivityPunchCard()
  const punchCardCallout = punchCardData?.data?.callout

  const rowRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)

  const { nav_links } = globalHeaderCategories

  const globalMoreCategories = nav_links.slice(itemsToDisplay)

  const visibleWidth = useNavOverflow({
    rowRef,
    containerRef: navRef,
    itemCount: nav_links.length,
    onChange: setItemsToDisplay,
  })

  return (
    <NavigationWrapper justify="start" ref={rowRef}>
      <NavItems ref={navRef} $visibleWidth={visibleWidth}>
        {nav_links.map((nav_link, index) => {
          const isClipped = index >= itemsToDisplay
          return (
            <HeaderItemLink
              href={nav_link.data.link.url}
              key={nav_link.data.heading}
              prefetch={false}
              aria-hidden={isClipped}
              tabIndex={isClipped ? -1 : undefined}
            >
              <HeaderItem
                isSelected={isNavLinkActive(nav_link.data.link.url, pathname, searchParams)}
                holidayGHP={holidayGHP}
                onClick={() =>
                  handleHeaderItemClick(nav_link.data.heading, index, nav_link.data.link.url)
                }
              >
                {nav_link.data.heading}
              </HeaderItem>
            </HeaderItemLink>
          )
        })}
      </NavItems>
      {globalMoreCategories.length > 0 && (
        <DynamicHeaderRefreshMoreNavigationItem
          globalMoreCategories={globalMoreCategories}
          holidayGHP={holidayGHP}
        />
      )}
      {!!punchCardCallout && (
        <ActivityPunchCardNavigationItem punchCardData={punchCardData} holidayGHP={holidayGHP} />
      )}
    </NavigationWrapper>
  )
}

const NavigationWrapper = styled(Row)`
  height: 100%;
  min-width: 0;
  overflow: hidden;
`

const NavItems = styled(Row)<{ $visibleWidth: number }>`
  min-width: 0;
  overflow: hidden;
  width: ${({ $visibleWidth }) => `${$visibleWidth}px`};
`

const HeaderItemLink = styled(NextLink)`
  padding: 0 ${itemPadding}px;

  &[aria-hidden='true'] {
    visibility: hidden;
  }
`

export const GlobalMobileNavigation = ({ globalHeaderCategories, holidayGHP }: Props) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { nav_links } = globalHeaderCategories

  return (
    <MobileRow className="fullWidth" align="center">
      {nav_links.map((nav_link, index) => {
        return (
          <HeaderItemLink
            href={nav_link.data.link.url}
            key={nav_link.data.heading}
            prefetch={false}
            onClick={() =>
              handleHeaderItemClick(nav_link.data.heading, index, nav_link.data.link.url)
            }
          >
            <HeaderItem
              isSelected={isNavLinkActive(nav_link.data.link.url, pathname, searchParams)}
              holidayGHP={holidayGHP}
            >
              {nav_link.data.heading}
            </HeaderItem>
          </HeaderItemLink>
        )
      })}
    </MobileRow>
  )
}

const MobileRow = styled(Row)`
  max-width: 100%;
  height: 100%;
  width: 100%;
  overflow: scroll hidden;
  scrollbar-width: none;
  text-wrap: nowrap;
`
